import { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X, ZoomIn } from "lucide-react";
import { useI18n } from "../../i18n";

// First 5 are real apartment photos (uploaded). Items 6-13 are placeholders
// until further photos are uploaded — captions are still rendered.
const REAL_IMAGES = [
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/b5ci0op9_Vista%20sulla%20Baia.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/k7asj1qs_Living%20e%20Cucina%20Open%20Space.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/eernfm7x_Zona%20Living.png",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/iv7mj6rh_Cucina%20attrezzata.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/kdlao2wh_Sapori%20di%20Sardegna.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/xggh78h2_Divano%20Letto.png",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/6hp3lu9q_Terrazza.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/tl2792qj_Dettagli%20Mediterranei.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/f9jqld61_Camera%20Matrimoniale.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/qq6ctt6y_Camera%20matrimoniale%202.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/gd0cgewz_Bagno%20in%20Camera.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/9ronbi8f_Camera%20Doppia.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/vfac9uf0_Bagno%20Moderno.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/zbqvl97y_Dettagli%20in%20Pietra.jpeg",
  "https://customer-assets.emergentagent.com/job_mediterranean-dawn/artifacts/copdrwun_Ingresso%20privato%20%26%20Cortile.jpeg",
];
const TOTAL = 15;

export default function Gallery() {
  const { t } = useI18n();
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [orient, setOrient] = useState({});

  const handleImgLoad = (i, e) => {
    const w = e.target.naturalWidth;
    const h = e.target.naturalHeight;
    if (!w || !h) return;
    const r = w / h;
    const o = r < 0.95 ? "v" : r > 1.2 ? "h" : "s";
    setOrient((prev) => (prev[i] === o ? prev : { ...prev, [i]: o }));
  };

  const items = Array.from({ length: TOTAL }, (_, i) => ({
    src: REAL_IMAGES[i] || null,
    caption: t.gallery.captions[i],
    index: i,
  }));

  const updateProgress = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Ensure the gallery starts at position 0 (respecting scroll-padding).
    el.scrollLeft = 0;
    setProgress(0);
    el.addEventListener("scroll", updateProgress, { passive: true });
    return () => el.removeEventListener("scroll", updateProgress);
  }, [updateProgress]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section id="gallery" className="relative py-24 md:py-32 bg-sand-50" data-testid="gallery-section">
      <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-8 mb-10 md:mb-14">
          <div>
            <span className="eyebrow">{t.gallery.eyebrow}</span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-charcoal mt-3"
              data-testid="gallery-title"
            >
              {t.gallery.title}
            </motion.h2>
            <p className="mt-4 md:mt-5 text-charcoal-soft max-w-xl text-sm md:text-base leading-relaxed">
              {t.gallery.sub}
            </p>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          ref={scrollerRef}
          className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory gap-5 md:gap-6 px-8 sm:px-14 md:px-20 lg:px-32 xl:px-40 scroll-pl-8 sm:scroll-pl-14 md:scroll-pl-20 lg:scroll-pl-32 xl:scroll-pl-40 pb-2"
          data-testid="gallery-scroller"
        >
          {items.map((it, i) => {
            return (
            <motion.figure
              key={i}
              data-card
              data-testid={`gallery-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08 }}
              className={`snap-start shrink-0 group ${
                orient[i] === "h"
                  ? "w-[92vw] sm:w-[78vw] md:w-[60vw] lg:w-[50vw]"
                  : "w-[80vw] sm:w-[44vw] md:w-[34vw] lg:w-[28vw]"
              }`}
              data-orient={orient[i] || 's'}
            >
              <button
                type="button"
                onClick={() => it.src && setLightbox(it)}
                className={`relative block w-full overflow-hidden rounded-sm bg-sand-100 shadow-[0_18px_50px_-25px_rgba(31,31,29,0.18)] p-3 md:p-4 ${
                  orient[i] === "h"
                    ? "aspect-[4/3]"
                    : "aspect-[3/4]"
                }`}
                data-testid={`gallery-image-btn-${i}`}
                aria-label={it.caption}
              >
                {it.src ? (
                  <div className="relative w-full h-full overflow-hidden bg-sand-50">
                    <img
                      src={it.src}
                      alt={it.caption}
                      loading="lazy"
                      onLoad={(e) => handleImgLoad(i, e)}
                      className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-[1400ms] ease-out group-hover:scale-[1.02]"
                    />
                    <span className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/5 transition-colors duration-700" />
                    <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-ivory/85 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <ZoomIn size={14} strokeWidth={1.4} />
                    </span>
                  </div>
                ) : (
                  <div className="relative w-full h-full flex flex-col items-center justify-center text-charcoal-mute bg-sand-50">
                    <span className="font-serif italic text-2xl md:text-3xl text-charcoal/35 text-center px-4">{it.caption}</span>
                    <span className="mt-3 text-[0.6rem] tracking-luxe uppercase text-charcoal-mute/70">{t.gallery.coming_soon}</span>
                  </div>
                )}
              </button>
              <figcaption className="mt-3 flex items-center justify-between text-[0.7rem] tracking-luxe uppercase text-charcoal-mute">
                <span className="truncate pr-3">{it.caption}</span>
                <span>{String(i + 1).padStart(2, "0")} / {String(TOTAL).padStart(2, "0")}</span>
              </figcaption>
            </motion.figure>
            );
          })}
        </div>

        {/* Arrows overlaid INSIDE the carousel — vertically centered, visible on mobile and desktop */}
        <button
          onClick={() => scrollBy(-1)}
          data-testid="gallery-prev"
          aria-label="Previous image"
          className="absolute left-3 md:left-12 lg:left-24 xl:left-32 top-[45%] -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-ivory/75 backdrop-blur-md border border-charcoal/10 flex items-center justify-center hover:bg-ivory hover:scale-105 transition-all duration-500 shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
        >
          <ArrowLeft size={16} strokeWidth={1.4} />
        </button>
        <button
          onClick={() => scrollBy(1)}
          data-testid="gallery-next"
          aria-label="Next image"
          className="absolute right-3 md:right-12 lg:right-24 xl:right-32 top-[45%] -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-ivory/75 backdrop-blur-md border border-charcoal/10 flex items-center justify-center hover:bg-ivory hover:scale-105 transition-all duration-500 shadow-[0_6px_18px_rgba(0,0,0,0.08)]"
        >
          <ArrowRight size={16} strokeWidth={1.4} />
        </button>

        <div className="mt-8 px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
          <div className="h-px bg-charcoal/10 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-charcoal transition-[width] duration-300"
              style={{ width: `${Math.max(6, progress * 100)}%` }}
              data-testid="gallery-progress"
            />
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[100] bg-charcoal/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 w-11 h-11 rounded-full bg-ivory/90 flex items-center justify-center hover:bg-ivory transition"
              onClick={() => setLightbox(null)}
              data-testid="gallery-lightbox-close"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.4} />
            </button>
            <motion.figure
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[82vh] object-contain" />
              <figcaption className="mt-4 text-center text-ivory/85 text-sm tracking-luxe uppercase">
                {lightbox.caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
